import React, { useEffect, useState } from "react";
import { AppBar, Toolbar, Box, Button, Menu, MenuItem } from "@mui/material";
import { Link as RouterLink, useLocation } from "react-router-dom";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import LoginRoundedIcon from "@mui/icons-material/LoginRounded";
import logo from "../static/media/logo.webp";
import NavbarMobile from "./Navbar/NavbarMobile";

const PRIMARY = "#06BBC7";
const BLACK = "#111827";
const YELLOW = "#F7C948";
const YELLOW_HOVER = "#efbd35";

const treatmentItems = [
  {
    label: "Estética Corporal",
    href: "/tratamientos/estetica-corporal",
  },
  {
    label: "Estética Facial",
    href: "/tratamientos/estetica-facial",
  },
];

const stylingItems = [
  {
    label: "Peluquería",
    href: "/peluqueria",
  },
];

const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [stylingAnchorEl, setStylingAnchorEl] = useState(null);

  const location = useLocation();

  const treatmentsOpen = Boolean(anchorEl);
  const stylingOpen = Boolean(stylingAnchorEl);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 40);
    };

    handleScroll();
    window.addEventListener("scroll", handleScroll);

    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const isActive = (path) => {
    if (path === "/") return location.pathname === "/";
    return location.pathname.startsWith(path);
  };

  const isTreatmentsActive = () => {
    return treatmentItems.some((item) => isActive(item.href));
  };

  const isStylingActive = () => {
    return stylingItems.some((item) => isActive(item.href));
  };

  const handleTreatmentsOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleTreatmentsClose = () => {
    setAnchorEl(null);
  };

  const handleStylingOpen = (event) => {
    setStylingAnchorEl(event.currentTarget);
  };

  const handleStylingClose = () => {
    setStylingAnchorEl(null);
  };

  const navButtonSx = (active) => ({
    color: active ? PRIMARY : BLACK,
    textTransform: "none",
    fontWeight: 700,
    fontSize: scrolled ? "0.95rem" : "1.02rem",
    px: 0.5,
    py: 0.8,
    borderRadius: 0,
    borderBottom: `2px solid ${active ? PRIMARY : "transparent"}`,
    transition: "all 0.2s ease",
    "&:hover": {
      color: PRIMARY,
      backgroundColor: "transparent",
      borderBottomColor: PRIMARY,
    },
  });

  return (
    <>
      <AppBar
        position="fixed"
        elevation={scrolled ? 1 : 0}
        sx={{
          backgroundColor: scrolled ? "rgba(255, 255, 255, 0.84)" : "#ffffff",
          backdropFilter: scrolled ? "blur(14px)" : "none",
          transition: "all 0.28s ease",
          borderBottom: "1px solid rgba(4, 123, 132, 0.1)",
          color: BLACK,
          zIndex: 1200,
        }}
      >
        <Toolbar
          sx={{
            minHeight: {
              xs: scrolled ? 62 : 78,
              md: scrolled ? 68 : 94,
            },
            px: {
              xs: 2,
              sm: 3,
              md: 7,
            },
            display: "flex",
            justifyContent: "space-between",
            transition: "all 0.28s ease",
          }}
        >
          <Box
            component={RouterLink}
            to="/"
            sx={{
              display: "flex",
              alignItems: "center",
              textDecoration: "none",
            }}
          >
            <Box
              component="img"
              src={logo}
              alt="Lily's Sunflowers"
              sx={{
                height: {
                  xs: scrolled ? 44 : 56,
                  md: scrolled ? 49 : 65,
                },
                width: "auto",
                display: "block",
                transition: "all 0.28s ease",
              }}
            />
          </Box>

          <Box
            sx={{
              display: {
                xs: "none",
                md: "flex",
              },
              alignItems: "center",
              gap: 2.5,
            }}
          >
            <Button component={RouterLink} to="/" sx={navButtonSx(isActive("/"))}>
              Inicio
            </Button>

            <Button
              component={RouterLink}
              to="/acerca-de-nosotros"
              sx={navButtonSx(isActive("/acerca-de-nosotros"))}
            >
              Acerca de nosotros
            </Button>

            <Button
              onClick={handleTreatmentsOpen}
              endIcon={<KeyboardArrowDownIcon />}
              sx={navButtonSx(isTreatmentsActive())}
            >
              Tratamientos
            </Button>

            <Menu
              anchorEl={anchorEl}
              open={treatmentsOpen}
              onClose={handleTreatmentsClose}
              disableScrollLock
              MenuListProps={{
                sx: {
                  py: 1,
                },
              }}
              PaperProps={{
                sx: {
                  mt: 1.2,
                  borderRadius: 3,
                  boxShadow: "none",
                  border: "1px solid rgba(4, 123, 132, 0.14)",
                  minWidth: 220,
                },
              }}
            >
              {treatmentItems.map((item) => (
                <MenuItem
                  key={item.label}
                  component={RouterLink}
                  to={item.href}
                  onClick={handleTreatmentsClose}
                  sx={{
                    color: isActive(item.href) ? PRIMARY : BLACK,
                    fontWeight: 700,
                    fontSize: "0.95rem",
                    py: 1.2,
                    textDecoration: "none",
                    "&:hover": {
                      color: PRIMARY,
                      backgroundColor: "rgba(6, 187, 199, 0.07)",
                    },
                  }}
                >
                  {item.label}
                </MenuItem>
              ))}
            </Menu>

            <Button
              onClick={handleStylingOpen}
              endIcon={<KeyboardArrowDownIcon />}
              sx={navButtonSx(isStylingActive())}
            >
              Estilismo
            </Button>

            <Menu
              anchorEl={stylingAnchorEl}
              open={stylingOpen}
              onClose={handleStylingClose}
              disableScrollLock
              MenuListProps={{
                sx: {
                  py: 1,
                },
              }}
              PaperProps={{
                sx: {
                  mt: 1.2,
                  borderRadius: 3,
                  boxShadow: "none",
                  border: "1px solid rgba(4, 123, 132, 0.14)",
                  minWidth: 190,
                },
              }}
            >
              {stylingItems.map((item) => (
                <MenuItem
                  key={item.label}
                  component={RouterLink}
                  to={item.href}
                  onClick={handleStylingClose}
                  sx={{
                    color: isActive(item.href) ? PRIMARY : BLACK,
                    fontWeight: 700,
                    fontSize: "0.95rem",
                    py: 1.2,
                    textDecoration: "none",
                    "&:hover": {
                      color: PRIMARY,
                      backgroundColor: "rgba(6, 187, 199, 0.07)",
                    },
                  }}
                >
                  {item.label}
                </MenuItem>
              ))}
            </Menu>

            <Button
              component={RouterLink}
              to="/tienda"
              sx={navButtonSx(isActive("/tienda"))}
            >
              Tienda
            </Button>

            <Button
              component={RouterLink}
              to="/agendar-cita"
              variant="contained"
              sx={{
                bgcolor: YELLOW,
                color: "#1f2937",
                px: 4,
                py: 1.18,
                borderRadius: "999px",
                textTransform: "none",
                fontWeight: 700,
                fontSize: "0.95rem",
                boxShadow: "none",
                "&:hover": {
                  bgcolor: YELLOW_HOVER,
                  boxShadow: "0 14px 28px rgba(247, 201, 72, 0.28)",
                },
              }}
            >
              Agendar Cita
            </Button>

            <Button
              component={RouterLink}
              to="/iniciar-sesion"
              aria-label="Iniciar sesión"
              sx={{
                minWidth: 44,
                width: 44,
                height: 44,
                borderRadius: "50%",
                color: BLACK,
                border: "1px solid rgba(17, 24, 39, 0.12)",
                backgroundColor: "rgba(6, 187, 199, 0.08)",
                p: 0,
                "&:hover": {
                  color: PRIMARY,
                  backgroundColor: "rgba(6, 187, 199, 0.13)",
                  borderColor: "rgba(6, 187, 199, 0.35)",
                },
              }}
            >
              <LoginRoundedIcon sx={{ fontSize: 22 }} />
            </Button>
          </Box>

          <NavbarMobile
            treatmentItems={treatmentItems}
            stylingItems={stylingItems}
            isActive={isActive}
            isTreatmentsActive={isTreatmentsActive}
            isStylingActive={isStylingActive}
          />
        </Toolbar>
      </AppBar>

      <Box
        sx={{
          height: {
            xs: 78,
            md: 94,
          },
        }}
      />
    </>
  );
};

export default Navbar;